/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { resolveStoreDoorState, type StoreProfileDraft } from '../../domain/storeProfileTypes';

type Props = {
  draft: StoreProfileDraft;
  canEdit: boolean;
  saving: boolean;
  compact?: boolean;
  onRequestForceClose: (durationMinutes?: number) => void;
  onRequestForceOpen: () => void;
  onRequestReturnToSchedule: () => void;
};

function Chip({
  label,
  onPress,
  disabled,
  primary,
  stretch,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  primary?: boolean;
  stretch?: boolean;
}) {
  return (
    <TouchableOpacity
      style={[
        local.chip,
        stretch && local.chipStretch,
        primary && local.chipPrimary,
        disabled && styles.disabled,
      ]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Text style={[local.chipText, primary && local.chipTextPrimary]}>{label}</Text>
    </TouchableOpacity>
  );
}

export function StoreDoorStatusCard({
  draft,
  canEdit,
  saving,
  compact,
  onRequestForceClose,
  onRequestForceOpen,
  onRequestReturnToSchedule,
}: Props) {
  const doorState = resolveStoreDoorState(draft);
  const paused = doorState === 'paused';
  const forcedOpen = doorState === 'forced_open';
  const open = doorState === 'open' || forcedOpen;
  const title = paused ? 'Pausada' : open ? 'Abierta' : 'Cerrada';
  const badgeTone = paused ? 'closed' : open ? 'open' : 'muted';
  const untilTime = draft.manualOverrideUntil
    ? new Date(draft.manualOverrideUntil).toLocaleTimeString('es-AR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : null;

  let lead: string;
  if (paused) {
    lead = untilTime
      ? `Pausada manualmente · reabre a las ${untilTime}`
      : 'Pausada manualmente.';
  } else if (forcedOpen) {
    lead = untilTime
      ? `Abierta manualmente · vuelve al horario a las ${untilTime}`
      : 'Abierta fuera de horario.';
  } else if (open) {
    lead = 'En horario de atención.';
  } else {
    lead = 'Fuera de horario · abre según tus horarios.';
  }

  return (
    <PaperSheet padded>
      <View style={local.row}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={local.titleRow}>
            <View
              style={[
                local.dot,
                badgeTone === 'open' && local.dotOpen,
                badgeTone === 'closed' && local.dotClosed,
              ]}
            />
            <Text style={local.big}>{title}</Text>
          </View>
          <Text style={[styles.caption, { marginTop: 2 }]}>{lead}</Text>
        </View>

        {canEdit ? (
          <View style={local.actionsInline}>
            {paused || forcedOpen ? (
              <Chip
                label="Volver al horario"
                primary
                onPress={onRequestReturnToSchedule}
                disabled={saving}
              />
            ) : open ? (
              <>
                <Chip
                  label="Pausar 1 h"
                  onPress={() => onRequestForceClose(60)}
                  disabled={saving}
                />
                <Chip
                  label="Pausar 3 h"
                  onPress={() => onRequestForceClose(180)}
                  disabled={saving}
                />
                <Chip
                  label="Cerrar"
                  onPress={() => onRequestForceClose()}
                  disabled={saving}
                />
              </>
            ) : (
              <Chip
                label="Abrir ahora"
                primary
                onPress={onRequestForceOpen}
                disabled={saving}
              />
            )}
          </View>
        ) : null}
      </View>
    </PaperSheet>
  );
}

const local = StyleSheet.create({
  compact: {
    padding: spacing.md,
    gap: spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionsInline: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  big: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    letterSpacing: -0.2,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    flexShrink: 0,
  },
  badgeOpen: { backgroundColor: 'rgba(21,128,61,0.12)' },
  badgeClosed: { backgroundColor: 'rgba(185,28,28,0.1)' },
  badgeMuted: { backgroundColor: ticketColors.paper2 },
  badgeText: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
  },
  badgeTextOpen: { color: ticketColors.ok },
  badgeTextClosed: { color: ticketColors.bad },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: ticketColors.ink2,
  },
  dotOpen: { backgroundColor: ticketColors.ok },
  dotClosed: { backgroundColor: ticketColors.bad },
  btnCol: {
    gap: 8,
    marginTop: 4,
  },
  btnRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    minHeight: 38,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipStretch: {
    flexBasis: '100%',
  },
  chipPrimary: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    textAlign: 'center',
  },
  chipTextPrimary: {
    color: ticketColors.goldInk,
  },
  hint: {
    fontSize: 12,
    lineHeight: 17,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
});
